// Set to true to force "Buy in the next Xh Ym to ship today" for testing (set back to false when done)
const FORCE_SAME_DAY_SHIPPING_FOR_TESTING = false;

function isShopifyCartPagePath() {
  var cartUrl = window.routes?.cart_url;
  if (!cartUrl) return false;
  try {
    return new URL(cartUrl, window.location.href).pathname === window.location.pathname;
  } catch (e) {
    return false;
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.shippingBannerTimer = null;
    this.healAttempts = 0;
    this.healInFlight = false;
    this.wasOpen = false;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.updateShippingBanner();
    this.setupShippingTooltip();
    this.observeForBlankOpens();
  }

  // --- Blank-drawer self-heal -------------------------------------------
  // The drawer gets opened by several code paths (theme ATC, upsell flow,
  // header icon, app scripts), and any of them can race the section render
  // and open a white panel. Rather than patch each race, the drawer watches
  // itself: while open, if its body is blank it refetches its own sections
  // from the Cart API and re-renders. Capped at 2 heals per open cycle.
  observeForBlankOpens() {
    if (this.blankObserver || typeof MutationObserver === 'undefined') return;
    this.blankObserver = new MutationObserver(() => {
      const isOpen = this.classList.contains('active');
      if (isOpen && !this.wasOpen) {
        this.healAttempts = 0;
        this.scheduleHealthChecks();
      }
      this.wasOpen = isOpen;
    });
    this.blankObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
  }

  scheduleHealthChecks() {
    [300, 900, 1800].forEach((ms) => {
      setTimeout(() => this.healIfBlank(), ms);
    });
  }

  looksBlank() {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return true;
    // textContent, not innerText: innerText reads '' on hidden elements.
    const text = (inner.textContent || '').replace(/\s+/g, ' ').trim();
    return text.length < 40;
  }

  healIfBlank() {
    if (!this.classList.contains('active')) return;
    if (!this.looksBlank()) return;
    if (this.healAttempts >= 2 || this.healInFlight) return;
    this.healAttempts += 1;
    this.refetchContents();
  }

  refetchContents() {
    let ids;
    try {
      ids = this.getSectionsToRender().map((s) => s.id);
    } catch (e) {
      ids = ['cart-drawer', 'cart-icon-bubble'];
    }
    this.healInFlight = true;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: {}, sections: ids.join(','), sections_url: window.location.pathname }),
    })
      .then((r) => r.json())
      .then((data) => {
        this.healInFlight = false;
        if (data && data.sections) this.renderContents(data);
      })
      .catch((e) => {
        this.healInFlight = false;
        console.warn('[cart-drawer] self-heal refetch failed', e);
      });
  }

  setupShippingTooltip() {
    const toggleTooltip = (icon, wrapperSelector) => {
      const wrapper = icon?.closest(wrapperSelector);
      const isOpen = wrapper?.classList.toggle('is-open');
      icon?.setAttribute('aria-expanded', isOpen);
      if (isOpen) {
        this.closeShippingTooltipOnClickOutside = (evt) => {
          if (!evt.target.closest(wrapperSelector)) {
            wrapper?.classList.remove('is-open');
            icon?.setAttribute('aria-expanded', 'false');
            document.removeEventListener('click', this.closeShippingTooltipOnClickOutside);
          }
        };
        document.addEventListener('click', this.closeShippingTooltipOnClickOutside);
      } else if (this.closeShippingTooltipOnClickOutside) {
        document.removeEventListener('click', this.closeShippingTooltipOnClickOutside);
      }
    };

    this.addEventListener('click', (e) => {
      const shippingIcon = e.target.closest('.cart-drawer__shipping-info-icon');
      const pillIcon = e.target.closest('.cart-item__description-pill-icon');
      if (shippingIcon) {
        e.preventDefault();
        toggleTooltip(shippingIcon, '.cart-drawer__shipping-info-trigger');
      } else if (pillIcon) {
        e.preventDefault();
        toggleTooltip(pillIcon, '.cart-item__pill-tooltip-trigger');
      }
    });

    this.addEventListener('keydown', (e) => {
      const shippingIcon = e.target.closest('.cart-drawer__shipping-info-icon');
      const pillIcon = e.target.closest('.cart-item__description-pill-icon');
      if ((shippingIcon || pillIcon) && (e.code === 'Enter' || e.code === 'Space')) {
        e.preventDefault();
        if (shippingIcon) toggleTooltip(shippingIcon, '.cart-drawer__shipping-info-trigger');
        else if (pillIcon) toggleTooltip(pillIcon, '.cart-item__pill-tooltip-trigger');
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.addEventListener('click', (event) => {
      if (!document.body.classList.contains('cart-page-variant')) {
        return;
      }
      event.preventDefault();
      if (!cartLink.dataset.cartDrawerA11y) {
        cartLink.dataset.cartDrawerA11y = 'true';
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
      }
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() !== 'SPACE') return;
      if (!document.body.classList.contains('cart-page-variant')) {
        return;
      }
      event.preventDefault();
      if (!cartLink.dataset.cartDrawerA11y) {
        cartLink.dataset.cartDrawerA11y = 'true';
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
      }
      this.open(cartLink);
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.updateShippingBanner();
    this.startShippingBannerTimer();
  }

  close() {
    this.classList.remove('active');
    this.querySelectorAll('.cart-drawer__shipping-info-trigger.is-open, .cart-item__pill-tooltip-trigger.is-open').forEach((el) => el.classList.remove('is-open'));
    if (this.closeShippingTooltipOnClickOutside) {
      document.removeEventListener('click', this.closeShippingTooltipOnClickOutside);
    }
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.stopShippingBannerTimer();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    // The HOST-level is-empty is server-rendered when the page loads with an
    // empty cart, and component-cart-drawer.css hides .drawer__inner off it
    // (line ~134). Clearing only the inner class left the freshly rendered
    // contents invisible - the blank white drawer on first add. add.js
    // responses carry no item_count, but rendering an add means the cart
    // cannot be empty; update.js responses carry it, so toggle truthfully.
    if (typeof parsedState.item_count === 'number') {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    } else {
      this.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;
      const inner =
        section.id === 'cart-icon-bubble'
          ? this.extractCartIconBubbleInner(sectionHtml) || this.getSectionInnerHTML(sectionHtml, section.selector)
          : this.getSectionInnerHTML(sectionHtml, section.selector);
      if (inner) sectionElement.innerHTML = inner;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (!isShopifyCartPagePath() && document.body.classList.contains('cart-page-variant')) {
        this.open();
      }
      // A render that raced or received no section HTML can still leave the
      // open drawer blank; the health checks refetch and repair it.
      this.scheduleHealthChecks();
      setTimeout(function () {
        try {
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'cart-drawer-render' } }));
          document.dispatchEvent(new CustomEvent('shopify:cart:update', { bubbles: true, detail: { source: 'cart-drawer-render' } }));
        } catch (e) {}
      }, 0);
    });
  }

  getEasternTimeParts() {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(new Date());
    const map = {};
    parts.forEach((part) => {
      map[part.type] = part.value;
    });

    return {
      weekday: map.weekday,
      hour: Number(map.hour),
      minute: Number(map.minute),
    };
  }

  /** Same-day copy + countdown: ET clock only (not cart contents). Visibility of the banner is Liquid (`cart_has_shippable_items`). */
  getShippingBannerMessage() {
    const { weekday, hour, minute } = this.getEasternTimeParts();
    const isBusinessDay = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(weekday);
    const totalMinutes = hour * 60 + minute;
    const cutOffMinutes = 11 * 60; // 11:00 ET

    // When forcing for testing: if past cutoff, simulate 8:30am ET to show countdown
    const useSimulatedTime = FORCE_SAME_DAY_SHIPPING_FOR_TESTING && (!isBusinessDay || totalMinutes >= cutOffMinutes);
    const effectiveMinutes = useSimulatedTime ? 8 * 60 + 30 : totalMinutes;

    if ((FORCE_SAME_DAY_SHIPPING_FOR_TESTING || (isBusinessDay && totalMinutes < cutOffMinutes)) && effectiveMinutes < cutOffMinutes) {
      const remainingMinutes = cutOffMinutes - effectiveMinutes;
      const hours = Math.floor(remainingMinutes / 60);
      const minutes = remainingMinutes % 60;
      return `Buy in the next ${hours}h ${minutes}m to ship today`;
    }

    return 'Buy now to ship next business day';
  }

  isSameDayShipping() {
    if (FORCE_SAME_DAY_SHIPPING_FOR_TESTING) return true;
    const { weekday, hour, minute } = this.getEasternTimeParts();
    const isBusinessDay = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(weekday);
    const totalMinutes = hour * 60 + minute;
    const cutOffMinutes = 11 * 60;
    return isBusinessDay && totalMinutes < cutOffMinutes;
  }

  /** Call after cart drawer HTML is replaced so the message matches the time rule (avoids stale placeholder until timer/open). */
  updateShippingBanner() {
    const bannerText = this.querySelector('.cart-drawer__shipping-text');
    const banner = this.querySelector('.cart-drawer__shipping-banner');
    if (!bannerText || !banner) return;
    bannerText.textContent = this.getShippingBannerMessage();
    banner.classList.toggle('is-same-day', this.isSameDayShipping());
  }

  startShippingBannerTimer() {
    this.stopShippingBannerTimer();
    this.shippingBannerTimer = window.setInterval(() => {
      this.updateShippingBanner();
    }, 30000);
  }

  stopShippingBannerTimer() {
    if (!this.shippingBannerTimer) return;
    window.clearInterval(this.shippingBannerTimer);
    this.shippingBannerTimer = null;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html || typeof html !== 'string') return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sel = selector || '.shopify-section';
    let el = doc.querySelector(sel);
    if (!el && sel === '.shopify-section') {
      el = doc.querySelector('[id^="shopify-section-"]');
    }
    if (el) return el.innerHTML;
    if (sel === '.shopify-section' && doc.body?.innerHTML) {
      return doc.body.innerHTML;
    }
    return '';
  }

  extractCartIconBubbleInner(html) {
    if (!html || typeof html !== 'string') return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const wrap =
      doc.querySelector('.shopify-section') ||
      doc.querySelector('[id^="shopify-section-"]') ||
      doc.querySelector('#shopify-section-cart-icon-bubble');
    let inner = wrap ? wrap.innerHTML.trim() : '';
    if (!inner && doc.body?.innerHTML) {
      inner = doc.body.innerHTML.trim();
    }
    if (!inner) return '';
    const probe = new DOMParser().parseFromString(`<div>${inner}</div>`, 'text/html');
    const nestedA = probe.querySelector('a#cart-icon-bubble');
    return nestedA ? nestedA.innerHTML : inner;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Checkout failsafe: Clarity logged dead clicks on the drawer Checkout button
// (clicks landing mid-render or against a detached form die silently). If a
// checkout click has not started a navigation within 600ms, go directly.
document.addEventListener('click', function (e) {
  var btn = e.target && e.target.closest && e.target.closest('#CartDrawer-Checkout');
  if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
  var started = false;
  var mark = function () { started = true; };
  window.addEventListener('beforeunload', mark, { once: true });
  setTimeout(function () {
    window.removeEventListener('beforeunload', mark);
    if (!started && !document.hidden) window.location.assign('/checkout');
  }, 450);
}, true);

// PD add-on pricing guard: the $100 "5.5 Hour PD Workshop - Class Kit Add-On"
// is a kit-buyer price (sold alone as Individual PD at $200). If the shopper
// removes every Class Kit but keeps the add-on, swap it for the full-price
// Individual PD and say so in the drawer.
(function () {
  var PD_ADDON = 62037890302322;
  var PD_INDIVIDUAL = 52310177546610;
  var KIT_VARIANTS = [51869185212786, 51956382597490, 51869185606002, 51956377878898,
    51869185376626, 51956381516146, 52313216287090, 52313216319858,
    51869184885106, 51956110688626];
  var checking = false;

  function noticeHtml() {
    var el = document.createElement('div');
    el.setAttribute('data-pd-guard-note', '');
    el.style.cssText = 'margin:10px 16px;padding:10px 14px;background:#FFF8E7;border:1px solid #f0e5c8;border-radius:10px;font-size:12.5px;line-height:1.5;color:#0B1C32;';
    el.textContent = 'Heads up: the $100 PD price is a Class Kit add-on. Without a kit in your cart, PD is the $200 Individual workshop (same 5.5 hours + certificate).';
    return el;
  }

  function guard() {
    if (checking) return;
    checking = true;
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var addon = cart.items.find(function (i) { return i.variant_id === PD_ADDON; });
        var hasKit = cart.items.some(function (i) { return KIT_VARIANTS.indexOf(i.variant_id) !== -1; });
        if (!addon || hasKit) { checking = false; return; }
        var updates = {};
        updates[PD_ADDON] = 0;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        })
          .then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: PD_INDIVIDUAL, quantity: addon.quantity }] })
            });
          })
          .then(function () {
            var drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.refetchContents === 'function') drawer.refetchContents();
            else if (drawer) {
              fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: {} }) });
            }
            setTimeout(function () {
              var body = document.querySelector('cart-drawer .drawer__inner');
              if (body && !body.querySelector('[data-pd-guard-note]')) body.prepend(noticeHtml());
            }, 900);
            checking = false;
          });
      })
      .catch(function () { checking = false; });
  }

  // Run after any cart mutation settles (item removal posts to /cart/change).
  var origFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0] && (arguments[0].url || arguments[0]);
    var p = origFetch.apply(this, arguments);
    if (typeof url === 'string' && /\/cart\/(change|update)/.test(url)) {
      p.then(function () { setTimeout(guard, 400); });
    }
    return p;
  };
  document.addEventListener('DOMContentLoaded', function () { setTimeout(guard, 1200); });
})();
